import { useState, useEffect } from 'react';
import { Brain, Users, Code, CheckCircle2, X, ChevronDown, ChevronUp } from 'lucide-react';
import { usePortfolio } from '@contexts/PortfolioContext';
import portfolioService from '@services/portfolio.service';

const ProfessionalSkillTracker = () => {
  const {
    selectedPersonalityTraits,
    professionalSoftSkills,
    selectedHardSkills,
    personalityTraits,
    updatePersonalityTraits,
    toggleProfessionalSoftSkill,
    toggleHardSkill,
    professionalTier,
  } = usePortfolio();

  const [hardSkillCategories, setHardSkillCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedSkills, setExpandedSkills] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [professionalSoftSkillsList, setProfessionalSoftSkillsList] = useState([]);
  const [loadingSoftSkills, setLoadingSoftSkills] = useState(false);
  const [softSkillsError, setSoftSkillsError] = useState(null);

  useEffect(() => {
    fetchHardSkills();
  }, []);

  useEffect(() => {
    if (professionalTier) {
      fetchProfessionalSoftSkills();
    }
  }, [professionalTier]);

  const fetchProfessionalSoftSkills = async () => {
    try {
      setLoadingSoftSkills(true);
      setSoftSkillsError(null);
      const response = await portfolioService.getProfessionalSoftSkills(professionalTier);
      const data = response?.data?.data || [];
      setProfessionalSoftSkillsList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching professional soft skills:', error);
      setSoftSkillsError('Failed to load professional soft skills. Please try again.');
      setProfessionalSoftSkillsList([]);
    } finally {
      setLoadingSoftSkills(false);
    }
  };

  const fetchHardSkills = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await portfolioService.getHardSkills({ grouped: true });
      const data = response?.data?.data || [];
      setHardSkillCategories(Array.isArray(data) ? data : []);
      if (data.length > 0) {
        setSelectedCategory(data[0].category);
      }
    } catch (error) {
      console.error('Error fetching hard skills:', error);
      setError('Failed to load hard skills. Please try again.');
      setHardSkillCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const togglePersonalityTrait = (trait) => {
    const isSelected = selectedPersonalityTraits.some(t => t.id === trait.id);
    if (isSelected) {
      updatePersonalityTraits(selectedPersonalityTraits.filter(t => t.id !== trait.id));
    } else {
      if (selectedPersonalityTraits.length < 20) {
        updatePersonalityTraits([...selectedPersonalityTraits, trait]);
      }
    }
  };

  const toggleSkillExpansion = (skillId) => {
    setExpandedSkills(prev => ({
      ...prev,
      [skillId]: !prev[skillId]
    }));
  };

  const currentCategorySkills = hardSkillCategories.find(
    c => c.category === selectedCategory
  )?.skills || [];

  const selectedHardSkillIds = selectedHardSkills.map(s => s.id);

  return (
    <div className="space-y-6">
      {/* Learner Characteristics (Personality Traits) */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-start gap-2.5 px-3 sm:px-4 py-2.5 border-b border-gray-100 bg-red-50">
          <div className="w-8 h-8 rounded-lg bg-red-50 border border-[#ef4444] flex items-center justify-center flex-shrink-0">
            <Brain className="w-4 h-4 text-[#ef4444]" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm sm:text-base font-bold text-gray-900">
              Learner Characteristics
            </h3>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
              Select personality traits that describe you (max 20)
            </p>
          </div>
        </div>

        <div className="p-3 sm:p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {personalityTraits.map((trait) => {
              const isSelected = selectedPersonalityTraits.some(t => t.id === trait.id);
              return (
                <button
                  key={trait.id}
                  type="button"
                  onClick={() => togglePersonalityTrait(trait)}
                  disabled={!isSelected && selectedPersonalityTraits.length >= 20}
                  className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                    isSelected
                      ? 'bg-purple-500 text-white shadow-sm'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-purple-400 hover:text-purple-600 disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  {trait.name}
                </button>
              );
            })}
          </div>

          {selectedPersonalityTraits.length > 0 && (
            <div className="border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs sm:text-sm font-semibold text-gray-800">
                  Selected ({selectedPersonalityTraits.length}/20)
                </span>
                <button
                  type="button"
                  onClick={() => updatePersonalityTraits([])}
                  className="text-xs font-semibold text-[#ef4444] hover:text-[#dc2626]"
                >
                  Clear all
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectedPersonalityTraits.map((trait) => (
                  <span
                    key={trait.id}
                    className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-red-50 border border-red-200 rounded-full text-xs text-red-700 font-medium"
                  >
                    {trait.name}
                    <button
                      type="button"
                      onClick={() => togglePersonalityTrait(trait)}
                      className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-600"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Professional Soft Skills */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-start gap-2.5 px-3 sm:px-4 py-2.5 border-b border-gray-100 bg-blue-100">
          <div className="w-8 h-8 rounded-lg bg-blue-100 border border-blue-600 flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm sm:text-base font-bold text-gray-900">
              Professional Soft Skills
            </h3>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
              Select the soft skills you possess
            </p>
          </div>
        </div>

        <div className="p-3 sm:p-4 space-y-3">
          {loadingSoftSkills ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : softSkillsError ? (
            <div className="text-center py-8 text-red-600 text-sm">
              {softSkillsError}
            </div>
          ) : !professionalTier ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              Please select your Professional Tier in the Portfolio tab to see relevant soft skills.
            </div>
          ) : professionalSoftSkillsList.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              No soft skills available for your professional tier.
            </div>
          ) : (
            <>
              <div className="max-h-[500px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-blue-300 scrollbar-track-gray-100">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                  {professionalSoftSkillsList.map((skill) => {
                  const isSelected = professionalSoftSkills.some(s => s.id === skill.id);
                  const isExpanded = expandedSkills[skill.id];
                  return (
                    <div
                      key={skill.id}
                      className={`border rounded-lg p-3 transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50/50'
                          : 'border-gray-200 bg-white hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1">
                          <h4 className="text-sm font-bold text-gray-900">{skill.skillName}</h4>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleProfessionalSoftSkill(skill)}
                          className={`flex-shrink-0 w-5 h-5 rounded flex items-center justify-center transition-all ${
                            isSelected
                              ? 'bg-blue-500 border border-blue-500'
                              : 'bg-white border border-gray-300'
                          }`}
                        >
                          {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">{skill.definition}</p>
                      {skill.example && (
                        <>
                          <button
                            type="button"
                            onClick={() => toggleSkillExpansion(skill.id)}
                            className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                          >
                            {isExpanded ? (
                              <>
                                <ChevronUp className="w-3 h-3" />
                                Hide Example
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-3 h-3" />
                                Show Example
                              </>
                            )}
                          </button>
                          {isExpanded && (
                            <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-100">
                              <p className="text-xs text-gray-700 italic">{skill.example}</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {!loadingSoftSkills && professionalSoftSkillsList.length > 0 && professionalSoftSkills.length > 0 && (
            <div className="border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs sm:text-sm font-semibold text-gray-800">
                  Selected ({professionalSoftSkills.length})
                </span>
                <button
                  type="button"
                  onClick={() => professionalSoftSkills.forEach(skill => toggleProfessionalSoftSkill(skill))}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                >
                  Clear all
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {professionalSoftSkills.map((skill) => (
                  <span
                    key={skill.id}
                    className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-700 font-medium"
                  >
                    {skill.skillName}
                    <button
                      type="button"
                      onClick={() => toggleProfessionalSoftSkill(skill)}
                      className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center bg-blue-100 hover:bg-blue-200 text-blue-600"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hard Skills */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-start gap-2.5 px-3 sm:px-4 py-2.5 border-b border-gray-100 bg-green-100">
          <div className="w-8 h-8 rounded-lg bg-green-100 border border-green-600 flex items-center justify-center flex-shrink-0">
            <Code className="w-4 h-4 text-green-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm sm:text-base font-bold text-gray-900">
              Hard Skills
            </h3>
            <p className="text-[11px] sm:text-xs text-gray-500 mt-0.5">
              Select your technical and domain-specific skills (max 20)
            </p>
          </div>
        </div>

        <div className="p-3 sm:p-4 space-y-4">
          {loading && (
            <div className="text-center py-8">
              <div className="inline-block w-8 h-8 border-4 border-green-200 border-t-green-500 rounded-full animate-spin"></div>
              <p className="text-sm text-gray-600 mt-2">Loading skills...</p>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          {!loading && !error && hardSkillCategories.length > 0 && (
            <>
              {/* Category Selection */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                  Category
                </p>
                <div className="flex flex-wrap gap-2">
                  {hardSkillCategories.map((cat) => {
                    const isActive = cat.category === selectedCategory;
                    return (
                      <button
                        key={cat.category}
                        type="button"
                        onClick={() => setSelectedCategory(cat.category)}
                        className={`px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold transition-all ${
                          isActive
                            ? 'bg-green-500 text-white shadow-sm'
                            : 'bg-white text-gray-700 border border-gray-200 hover:border-green-400 hover:text-green-600'
                        }`}
                      >
                        {cat.category}
                        <span className={`ml-1.5 text-[10px] ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                          ({cat.count})
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Skills List with Expand/Collapse */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                  Skills in {selectedCategory}
                </p>
                <div className="max-h-[500px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-green-300 scrollbar-track-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2">
                  {currentCategorySkills.map((skill) => {
                    const isSelected = selectedHardSkillIds.includes(skill.id);
                    const isExpanded = expandedSkills[skill.id];
                    
                    return (
                      <div
                        key={skill.id}
                        className={`border rounded-lg transition-all ${
                          isSelected
                            ? 'border-green-500 bg-green-50/60'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-start gap-3 p-3">
                          <button
                            type="button"
                            onClick={() => toggleHardSkill({
                              id: skill.id,
                              name: skill.name,
                              category: selectedCategory,
                              definition: skill.definition
                            })}
                            disabled={!isSelected && selectedHardSkills.length >= 20}
                            className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                              isSelected
                                ? 'bg-green-500 border border-green-500'
                                : 'bg-white border border-gray-300 hover:border-green-400 disabled:opacity-50 disabled:cursor-not-allowed'
                            }`}
                          >
                            {isSelected && (
                              <CheckCircle2 className="w-3 h-3 text-white" />
                            )}
                          </button>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <h4 className="text-sm font-semibold text-gray-900">
                                {skill.name}
                              </h4>
                              <button
                                type="button"
                                onClick={() => toggleSkillExpansion(skill.id)}
                                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                              >
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </button>
                            </div>
                            
                            {isExpanded && (
                              <p className="text-xs text-gray-600 mt-2 leading-relaxed">
                                {skill.definition}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                </div>
              </div>

              {/* Selected Hard Skills Summary */}
              {selectedHardSkills.length > 0 && (
                <div className="border-t border-gray-100 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs sm:text-sm font-semibold text-gray-800">
                      Selected Hard Skills ({selectedHardSkills.length}/20)
                    </span>
                    <button
                      type="button"
                      onClick={() => selectedHardSkills.forEach(skill => toggleHardSkill(skill))}
                      className="text-xs font-semibold text-green-600 hover:text-green-700"
                    >
                      Clear all
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedHardSkills.map((skill) => (
                      <span
                        key={skill.id}
                        className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-green-50 border border-green-200 rounded-full text-xs text-green-700 font-medium"
                      >
                        <span className="text-gray-500 text-[10px]">{skill.category}</span>
                        <span className="text-gray-400">/</span>
                        {skill.name}
                        <button
                          type="button"
                          onClick={() => toggleHardSkill(skill)}
                          className="ml-0.5 w-4 h-4 rounded-full flex items-center justify-center bg-green-100 hover:bg-green-200 text-green-600"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {!loading && !error && hardSkillCategories.length === 0 && (
            <div className="text-center py-8 text-gray-500 text-sm">
              No hard skills available.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfessionalSkillTracker;
